import React, {
	useState,
	useEffect,
	useMemo,
	useCallback,
	useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router';
import { useFeature, useGrowthBook } from '@growthbook/growthbook-react';

import {
	GenericAPIResponse,
	RootState,
	useAppDispatch,
} from '@app/redux/types';
import { growthbookFeatures } from '@app/constants/growthbook';

import {
	filterNavLinks,
	formatInboxOrder,
	getNavBarInitialState,
	sortUserTeams,
} from './utils';

import { PageRouteEnum } from '@app/constants/pages';
import {
	NavBarState,
	NavBarDimension,
	NavLinkIconName,
	NavLinkText,
} from './constants';

import * as S from './NavigationBar.styles';
import GoodkindTheme from '@global/theme';

import { Divider, Icon } from '@nicheinc/campus-ds';
import { Logo } from '@global/design-system/atoms';
import { getTeams } from '@app/redux/teams/action';
import {
	TeamStorytellerType,
	TeamType,
	TeamWithStorytellerInfoType,
} from '@app/types/modules/teams';

import {
	NavLinkButton,
	NavLinkWithSubMenu,
	type NavLinkButtonProps,
} from '@global/design-system/molecules';
import { PopupMenu, PopupButton } from './components';
import {
	checkWorkspaceHasEmbeddedReelsPlan,
	checkWorkspaceHasReelsPlan,
	checkWorkspaceHasSMSPlan,
	checkWorkspaceHasVideoMessagingPlan,
	checkWorkspaceIsChatbotOnly,
} from '@app/utils/modules/workspace';
import { useIsAdvanceOnly } from '@app/hooks/modules/workspace/useIsAdvanceOnly';

import {
	checkIsWorkspaceAdmin,
	getStorytellerName,
} from '@app/utils/modules/storytellers';
import { useAuth } from '@app/services/Auth/useAuth';
import isEmptyValue from '@app/utils/isEmptyValue';
import { updateInboxOrder } from '@app/redux/storyteller/action';
import isSuccessHttpStatus from '@app/utils/isSuccessHttpStatus';
import { UPDATE_USER_INBOX_ORDER } from '@app/redux/auth';
import { InboxOrder } from '@app/redux/storyteller/types';

export default function NewNavigationBar() {
	const dispatch = useAppDispatch();

	const navigate = useNavigate();

	const location = useLocation();

	const growthbook = useGrowthBook();

	const isPerplexityEnabled = useFeature(
		growthbookFeatures.CHATBOT_PERPLEXITY_WEBSEARCH,
	).on;

	const isChatbotLeadFormsOn = useFeature(
		growthbookFeatures.CHATBOT_LEAD_FORMS,
	).on;
	const isCampaignLeadFormsOn = useFeature(
		growthbookFeatures.CAMPAIGN_LEAD_FORMS,
	).on;
	const isInquiryFormBuilderOn = isChatbotLeadFormsOn || isCampaignLeadFormsOn;

	const [navBarState, setNavBarState] = useState<string>(
		getNavBarInitialState(localStorage.getItem('navBarState') as NavBarState),
	);
	const [openedSubMenus, setOpenedSubMenus] = useState<string[]>([]);

	const [inboxOrder, setInboxOrder] = useState<InboxOrder[] | undefined>();

	const isNavbarCollapsed = navBarState === NavBarState.CLOSED;

	const { user, isAccessLoading } = useAuth();

	const isWorkspaceAdmin = checkIsWorkspaceAdmin(user);

	const workspaceInfo = useSelector(
		(state: RootState) => state.workspace.workspaceInfo,
	);

	const hasSMSPlan = checkWorkspaceHasSMSPlan(workspaceInfo);
	const hasVideoMessagingPlan =
		checkWorkspaceHasVideoMessagingPlan(workspaceInfo);

	const hasReelsPlan = checkWorkspaceHasReelsPlan(workspaceInfo);
	const hasEmbeddedReelsPlan =
		checkWorkspaceHasEmbeddedReelsPlan(workspaceInfo);
	const isAdvanceOnlyWorkspace = useIsAdvanceOnly();
	const isChatbotOnly = checkWorkspaceIsChatbotOnly(workspaceInfo);

	// Defer rendering the nav links until GrowthBook resolves (or a short
	// fallback elapses)
	const [isNavReadyFallbackElapsed, setIsNavReadyFallbackElapsed] =
		useState(false);

	useEffect(() => {
		if (growthbook?.ready) return;
		const timeout = setTimeout(() => setIsNavReadyFallbackElapsed(true), 1500);
		return () => clearTimeout(timeout);
	}, [growthbook?.ready]);

	const isNavReady =
		!!workspaceInfo?._id && (!!growthbook?.ready || isNavReadyFallbackElapsed);

	const allTeams: TeamType[] = useSelector(({ teams }: RootState) =>
		Object.values(teams.teams),
	);

	const totalTeamsUnread = useSelector(
		(state: RootState) => state.teams.totalUnreads,
	);

	const allTeamsIndex = useMemo(() => {
		const index = new Map<string, TeamType>();
		allTeams.forEach((t) => {
			if (t?._id) index.set(t._id, t);
			if (t?.id) index.set(t.id, t);
		});
		return index;
	}, [allTeams]);

	// Support method to map the team from the user to the complete team and merge the data
	// (team info: name, icon.. + storyteller team related info: role, permissions, notifications..)
	const mapTeamToCompleteTeam = useCallback(
		(team: {
			_id?: string;
			id?: string;
			storytellers?: TeamStorytellerType[];
		}): TeamWithStorytellerInfoType | null => {
			const key = team._id || team.id || '';
			const completeTeam = key ? allTeamsIndex.get(key) : undefined;

			if (!completeTeam) {
				return null;
			}

			if (completeTeam?.hideInboxInApp) {
				return null;
			}

			const { storytellers } = team;

			return {
				...completeTeam,
				storyteller: storytellers?.[0]?.storyteller,
				permissions: storytellers?.[0]?.permissions,
				notifications: storytellers?.[0]?.notifications,
				role: storytellers?.[0]?.role,
			};
		},
		[allTeamsIndex],
	);

	const commonNavLinkProps = useMemo(
		() => ({ isDisabled: isAccessLoading, isNavbarCollapsed }),
		[isAccessLoading, isNavbarCollapsed],
	);
	const listOfTeamsToUse = isWorkspaceAdmin ? allTeams : user?.teams;

	// User teams with the complete team info
	const userTeams = useMemo(() => {
		const filteredTeams = listOfTeamsToUse
			?.map(mapTeamToCompleteTeam)
			.filter(Boolean) as (TeamType & {
			storyteller: string;
			permissions: TeamStorytellerType['permissions'];
			notifications: TeamStorytellerType['notifications'];
		})[];
		const inboxOrder = user?.settings?.inboxOrders?.[0]?.inboxOrder || [];
		if (inboxOrder && inboxOrder?.length === filteredTeams?.length) {
			return sortUserTeams(inboxOrder, filteredTeams);
		}
		return filteredTeams;
	}, [user, listOfTeamsToUse, mapTeamToCompleteTeam]);

	const totalUnreadsToShow = useMemo(() => {
		if (isWorkspaceAdmin) return totalTeamsUnread;
		return (
			userTeams?.reduce(
				(acc, team) => acc + (team?.unreadConversations || 0),
				0,
			) || 0
		);
	}, [isWorkspaceAdmin, totalTeamsUnread, userTeams]);

	const hasAnyTeamInboxPermission = userTeams?.some(
		(team) => team?.permissions?.teamInbox,
	);

	const formatInboxMenuOption = useCallback(
		(teams: TeamWithStorytellerInfoType[]) => {
			return teams
				?.filter((team) => isWorkspaceAdmin || team.permissions?.teamInbox)
				?.map((team: any) => {
					const isActive = location.pathname.includes(
						`/${PageRouteEnum.inbox}/${team.id}`,
					);
					return {
						text: team.name,
						iconName: team.icon,
						count: team?.unreadConversations || 0,

						// while preserving current query params (e.g. list tab).
						redirectTo: `/${PageRouteEnum.inbox}/${team.id}${location.search || ''}`,
						active: isActive,
						type: isActive ? 'primary' : 'regular',
						...commonNavLinkProps,
					};
				});
		},
		[commonNavLinkProps, isWorkspaceAdmin, location.pathname, location.search],
	);

	const inboxOptionMenuSorted = useMemo(() => {
		if (!inboxOrder) {
			return formatInboxMenuOption(userTeams);
		}

		const sortedUserTeams = sortUserTeams(inboxOrder, userTeams);
		return formatInboxMenuOption(sortedUserTeams);
	}, [userTeams, formatInboxMenuOption, inboxOrder]);

	const handleSortInboxMenu = async (sortedMenus: NavLinkButtonProps[]) => {
		const prevInboxOrder = inboxOrder;
		const newInboxOrder = formatInboxOrder(sortedMenus);
		setInboxOrder(newInboxOrder);
		try {
			const response = (await dispatch(
				updateInboxOrder(newInboxOrder),
			)) as unknown as GenericAPIResponse;
			if (isSuccessHttpStatus(response.status)) {
				const { inboxOrder } = response;
				dispatch(UPDATE_USER_INBOX_ORDER({ inboxOrder }));
			} else {
				const errorInfo = response?.metadata;
				if (errorInfo) {
					// If we have controlled error from backend we are receiving the saved inboxOrder in the error metadata
					// So we set the user preference and clear the changes in the state.
					// That way we are showing the state saved in the db.
					const { inboxOrder } = errorInfo;
					dispatch(UPDATE_USER_INBOX_ORDER({ inboxOrder }));
					setInboxOrder(undefined);
				}
			}
		} catch {
			setInboxOrder(prevInboxOrder);
		}
	};
	const isShowingSuccessPlanNav = useMemo(
		() =>
			!!workspaceInfo?.settings?.dockUrl &&
			!isEmptyValue(workspaceInfo?.settings?.dockUrl) &&
			isWorkspaceAdmin,
		[workspaceInfo, isWorkspaceAdmin],
	);

	const handleNavBarStateChange = useCallback(() => {
		if (navBarState === NavBarState.OPEN) {
			setNavBarState(NavBarState.CLOSED);
		} else if (isNavbarCollapsed) {
			setNavBarState(NavBarState.OPEN);
		}
	}, [navBarState, isNavbarCollapsed]);
	// Listener to update localStorage on NavBar state change
	useEffect(() => {
		const onResize = () => {
			if (window.innerWidth <= GoodkindTheme.breakpoint.width.m) {
				setNavBarState(NavBarState.CLOSED);
			}
		};
		window.addEventListener('resize', onResize, true);
		return () => window.removeEventListener('resize', onResize, true);
	}, []);

	const hasRequestedTeams = useRef(false);

	// Get teams once the workspace has loaded
	useEffect(() => {
		// No workspace yet, or chatbot-only workspaces don't use teams
		if (!workspaceInfo?._id || isChatbotOnly) return;

		// Only fetch a single time
		if (hasRequestedTeams.current) return;

		hasRequestedTeams.current = true;
		dispatch(getTeams());
	}, [dispatch, workspaceInfo?._id, isChatbotOnly]);

	// Listener to update localStorage on NavBar state change
	useEffect(() => {
		if (navBarState === NavBarState.OPEN) {
			localStorage.setItem('navBarState', NavBarState.OPEN);
		} else if (isNavbarCollapsed) {
			localStorage.setItem('navBarState', NavBarState.CLOSED);
		}
	}, [navBarState]);

	const handelRedirectDock = useCallback(() => {
		const dockUrl = (workspaceInfo?.settings?.dockUrl as string) || '';
		window.open(dockUrl, '_blank');
	}, [workspaceInfo?.settings?.dockUrl]);

	const handleToggleOpenedSubMenu = useCallback(
		(navLinkText: NavLinkText) => {
			const navLinkExists = openedSubMenus?.includes(navLinkText);

			if (navLinkExists) {
				setOpenedSubMenus((prev) =>
					prev?.filter((text) => text !== navLinkText),
				);
			} else {
				setOpenedSubMenus((prev) => [...prev, navLinkText]);
			}
		},
		[openedSubMenus],
	);

	const primaryNavLinksGroup: NavLinkButtonProps[] = useMemo(() => {
		return [
			{
				text: NavLinkText.HOME,
				iconName: NavLinkIconName.HOME,
				redirectTo: `/${PageRouteEnum.home}`,
				...commonNavLinkProps,
			},

			{
				text: isPerplexityEnabled
					? NavLinkText.NICHECHATBOT
					: NavLinkText.CHATBOT,
				iconName: NavLinkIconName.CHATBOT_INACTIVE,
				redirectTo: `/${PageRouteEnum.chatbot}`,
				...commonNavLinkProps,
			},
			...(!isAdvanceOnlyWorkspace
				? [
						{
							text: NavLinkText.ACTIVITY,
							iconName: NavLinkIconName.ACTIVITY,
							redirectTo: `/${PageRouteEnum.activity}`,
							...commonNavLinkProps,
						},
					]
				: []),
			{
				text: NavLinkText.CAMPAIGNS,
				iconName: NavLinkIconName.CAMPAIGNS,
				redirectTo: `/${PageRouteEnum.campaigns}`,
				...commonNavLinkProps,
			},
			{
				text: NavLinkText.VIDEOS,
				iconName: NavLinkIconName.VIDEOS,
				redirectTo: `/${PageRouteEnum.videos}`,
				...commonNavLinkProps,
			},
			{
				text: NavLinkText.RECORDERS,
				iconName: NavLinkIconName.RECORDERS,
				redirectTo: `/${PageRouteEnum.recorders}`,
				...commonNavLinkProps,
			},

			{
				text: NavLinkText.SUCCESS_PLAN,
				iconName: NavLinkIconName.SUCCESS_PLAN,
				redirectTo: '',
				onClick: handelRedirectDock,
				redirectLinkIcon: true,
				...commonNavLinkProps,
			},
			...(isAdvanceOnlyWorkspace
				? [
						{
							text: NavLinkText.AUDIENCES,
							iconName: NavLinkIconName.AUDIENCES,
							redirectTo: `/${PageRouteEnum.audiences}`,
							...commonNavLinkProps,
						},
					]
				: []),

			...(!isAdvanceOnlyWorkspace
				? ([
						{
							showMenuCount: false,
							hideOnCollapse: false,
							text: NavLinkText.AUDIENCES,
							iconName: NavLinkIconName.AUDIENCE,
							redirectTo: `/${PageRouteEnum.audiences}`,
							onClick: () => handleToggleOpenedSubMenu(NavLinkText.AUDIENCES),
							isCollapsed: openedSubMenus.includes(NavLinkText.AUDIENCES),
							collapseArrowDirection: 'right',
							isSubMenusThreaded: isNavbarCollapsed ? false : true,
							parentMenuCapitalize: false,
							parentMenuStyle: 'main' as const,
							active:
								!openedSubMenus?.includes(NavLinkText.AUDIENCES) &&
								location.pathname.includes(`/${PageRouteEnum.audiences}`),
							handleIsCollapsed: () =>
								handleToggleOpenedSubMenu(NavLinkText.AUDIENCES),
							menus: [
								{
									text: 'All contacts',
									iconName: 'usersFour',
									redirectTo: `/${PageRouteEnum.audiences}/contacts`,
									active:
										location.pathname ===
										`/${PageRouteEnum.audiences}/contacts`,
								},
								{
									text: 'Lists',
									iconName: 'user-list',
									redirectTo: `/${PageRouteEnum.audiences}`,
									active:
										location.pathname.includes(`/${PageRouteEnum.audiences}`) &&
										location.pathname !==
											`/${PageRouteEnum.audiences}/contacts`,
								},
							],
							...commonNavLinkProps,
						},
					] as NavLinkButtonProps[])
				: []),
			...(isInquiryFormBuilderOn && !isAdvanceOnlyWorkspace
				? [
						{
							text: NavLinkText.INQUIRIES,
							iconName: NavLinkIconName.INQUIRIES,
							redirectTo: `/${PageRouteEnum.inquiries}`,
							...commonNavLinkProps,
						},
					]
				: []),
		];
	}, [
		commonNavLinkProps,
		handelRedirectDock,
		handleToggleOpenedSubMenu,
		isInquiryFormBuilderOn,
		isNavbarCollapsed,
		isAdvanceOnlyWorkspace,
		location.pathname,
		openedSubMenus,
	]);

	const secondaryNavLinksGroup: NavLinkButtonProps[] = useMemo(() => {
		return [
			...(!!inboxOptionMenuSorted?.length
				? [
						{
							showMenuCount: true,
							text: NavLinkText.INBOX,
							iconName: isNavbarCollapsed ? NavLinkIconName.INBOX : '',
							redirectTo: `/${PageRouteEnum.inbox}`,
							isCollapsed: openedSubMenus.includes(NavLinkText.INBOX),
							count: totalUnreadsToShow || 0,
							active:
								!openedSubMenus?.includes(NavLinkText.INBOX) &&
								location.pathname.includes(`/${PageRouteEnum.inbox}`),
							handleIsCollapsed: () =>
								handleToggleOpenedSubMenu(NavLinkText.INBOX),
							menus: inboxOptionMenuSorted,
							...commonNavLinkProps,
						},
					]
				: []),
		];
	}, [
		openedSubMenus,
		location.pathname,
		isNavbarCollapsed,
		totalUnreadsToShow,
		inboxOptionMenuSorted,
		commonNavLinkProps,
		isAdvanceOnlyWorkspace,
	]);

	const filteredPrimaryNavLinks = useMemo(() => {
		if (!isNavReady) return [];
		return filterNavLinks(
			primaryNavLinksGroup,
			{
				showSuccessPlanMenu: isShowingSuccessPlanNav,
				hasAnyTeamInboxPermission,
				isWorkspaceAdmin,
				showChatbotMenu: true,
				hasVideoMessagingPlan,
				hasSMSPlan,
				hasReelsPlan,
				hasEmbeddedReelsPlan,
			},
			growthbook,
		);
	}, [
		isNavReady,
		primaryNavLinksGroup,
		hasAnyTeamInboxPermission,
		isWorkspaceAdmin,
		hasVideoMessagingPlan,
		hasSMSPlan,
	]);

	const filteredSecondaryNavLinks = useMemo(() => {
		if (!isNavReady) return [];
		return filterNavLinks(secondaryNavLinksGroup, {
			hasAnyTeamInboxPermission,
			isWorkspaceAdmin,
			hasVideoMessagingPlan,
			hasSMSPlan,
		});
	}, [
		isNavReady,
		secondaryNavLinksGroup,
		hasAnyTeamInboxPermission,
		isWorkspaceAdmin,
		hasVideoMessagingPlan,
		hasSMSPlan,
	]);

	// Open the inbox menu by default
	useEffect(() => {
		setOpenedSubMenus([NavLinkText.INBOX]);
	}, []);

	useEffect(() => {
		const isAudiencePage = location.pathname.includes(
			`/${PageRouteEnum.audiences}`,
		);
		const isInboxPage = location.pathname.includes(`/${PageRouteEnum.inbox}`);

		const isAudienceSubMenuOpen = openedSubMenus.includes(
			NavLinkText.AUDIENCES,
		);
		const isInboxSubMenuOpen = openedSubMenus.includes(NavLinkText.INBOX);

		const menusToOpen: NavLinkText[] = [];
		if (isAudiencePage && !isAudienceSubMenuOpen) {
			menusToOpen.push(NavLinkText.AUDIENCES);
		}

		if (isInboxPage && !isInboxSubMenuOpen) {
			menusToOpen.push(NavLinkText.INBOX);
		}

		if (menusToOpen.length) {
			setOpenedSubMenus((prev) => [...prev, ...menusToOpen]);
		}
	}, [location.pathname]);

	return (
		<S.NavigationBarWrapper
			isOpen={navBarState === NavBarState.OPEN}
			maxWidth={NavBarDimension.MAX_WIDTH}
			minWidth={NavBarDimension.MIN_WIDTH}
		>
			<S.NavigationBarContainer
				isOpen={navBarState === NavBarState.OPEN}
				maxWidth={NavBarDimension.MAX_WIDTH}
				minWidth={NavBarDimension.MIN_WIDTH}
			>
				<S.NavigationBarContent>
					{navBarState === NavBarState.OPEN && (
						<S.LogoWrapper>
							<S.LogoContainer
								onClick={() => navigate(`/${PageRouteEnum.home}`)}
							>
								<Logo />
							</S.LogoContainer>
							<S.CollapseCloseButton onClick={handleNavBarStateChange}>
								<Icon
									iconName="arrowLineLeft"
									size={'medium'}
									weight="regular"
									color="system.text.weak"
								/>
							</S.CollapseCloseButton>
						</S.LogoWrapper>
					)}

					{isNavbarCollapsed && (
						<S.CollapseOpenButton onClick={handleNavBarStateChange}>
							<Icon
								iconName="list"
								size={'large'}
								weight="regular"
								color="system.text.weak"
							/>
						</S.CollapseOpenButton>
					)}

					<S.NavigationLinksWrapper>
						<S.NavLinksGroup>
							<>
								{filteredPrimaryNavLinks.map((navLink: NavLinkButtonProps) => {
									const isSubMenuOpen = openedSubMenus?.includes(navLink.text);
									const isInboxMenu = navLink.text === NavLinkText.INBOX;
									const isShowingSubMenu = !(
										isNavbarCollapsed && navLink.hideOnCollapse
									);

									if (isShowingSubMenu && !!navLink?.menus?.length) {
										return (
											<NavLinkWithSubMenu
												showMenuCount={navLink?.showMenuCount}
												key={`nav-link-${navLink.text}`}
												navLink={navLink}
												isSubMenuOpen={isSubMenuOpen}
												isNavbarCollapsed={isNavbarCollapsed}
												loading={isAccessLoading}
												capitalizeTitle={navLink.parentMenuCapitalize}
												{...(isInboxMenu &&
												isShowingSubMenu &&
												!isNavbarCollapsed &&
												// allowing user to sort inbox menus if there are more than one
												navLink.menus.length > 1
													? {
															isMenuSortable: true,
															onSortedMenu: handleSortInboxMenu,
														}
													: {})}
											/>
										);
									}

									return (
										<NavLinkButton
											key={`nav-link-${navLink.text}`}
											{...navLink}
										/>
									);
								})}
							</>
						</S.NavLinksGroup>

						{isNavbarCollapsed && <Divider />}

						<S.NavLinksGroup>
							<>
								{filteredSecondaryNavLinks.map(
									(navLink: NavLinkButtonProps) => {
										const isSubMenuOpen = openedSubMenus?.includes(
											navLink.text,
										);
										const isInboxMenu = navLink.text === NavLinkText.INBOX;
										const isShowingSubMenu = !(
											isNavbarCollapsed && navLink.hideOnCollapse
										);

										if (isShowingSubMenu && !!navLink?.menus?.length) {
											return (
												<NavLinkWithSubMenu
													showMenuCount={navLink?.showMenuCount}
													key={`nav-link-${navLink.text}`}
													navLink={navLink}
													isSubMenuOpen={isSubMenuOpen}
													isNavbarCollapsed={isNavbarCollapsed}
													loading={isAccessLoading}
													capitalizeTitle={navLink.parentMenuCapitalize}
													{...(isInboxMenu &&
													isShowingSubMenu &&
													!isNavbarCollapsed &&
													// allowing user to sort inbox menus if there are more than one
													navLink.menus.length > 1
														? {
																isMenuSortable: true,
																onSortedMenu: handleSortInboxMenu,
															}
														: {})}
												/>
											);
										}

										return (
											<NavLinkButton
												key={`nav-link-${navLink.text}`}
												{...navLink}
											/>
										);
									},
								)}
							</>
						</S.NavLinksGroup>
					</S.NavigationLinksWrapper>

					<S.NavLinkBottomGroup>
						<PopupMenu
							trigger={(isPopupMenuOpen: boolean) => (
								<PopupButton
									isFocused={isPopupMenuOpen}
									userFullName={getStorytellerName(user)}
									userProfilePicture={user?.profilePicture}
									isNavbarCollapsed={isNavbarCollapsed}
								/>
							)}
						/>
					</S.NavLinkBottomGroup>
				</S.NavigationBarContent>
			</S.NavigationBarContainer>
		</S.NavigationBarWrapper>
	);
}
